import { Store } from '@ngrx/store';
import { EMPTY, interval, merge, Observable, Subject } from 'rxjs';
import { catchError, exhaustMap, map, startWith, take, takeUntil, takeWhile, tap } from 'rxjs/operators';
import { PlatformQueryService } from '@core/services/platform-query.service';
import { PlatformCommandResponse } from '@shared/commands/command-base';
import { Api } from '@shared/constants/common-field.constants';
import { PlatformQuery } from '@shared/models/platform-query/platform-query';
import { SecurityState } from '@shared/store/reducers/security.reducer';
import { getClaims } from '@shared/store/selectors/security.selector';

/** How often a home page asks again while a workspace Sync runs. */
export const WorkspaceSyncPollMs = 5000;
/** How long to follow a confirmed Sync that may still show the old bar, or to keep retrying failed asks. */
export const WorkspaceSyncFollowMs = 60000;

const asksPerFollow = WorkspaceSyncFollowMs / WorkspaceSyncPollMs;

/** Follows a workspace Sync on a home page, re-asking WorkspaceSyncQuery until it is done or failed. */
export class WorkspaceSyncFollower {
  private readonly restart = new Subject<void>();
  /** The last answer the page was given, so a confirmed Sync is followed until the bar differs from it. */
  private shown: any;

  constructor(
    private readonly securityStore: Store<SecurityState>,
    private readonly platformQueryService: PlatformQueryService,
    private readonly destroyed: Observable<void>,
    private readonly show: (response: any) => void
  ) { }

  /** Asks now, and again while the answer says the Sync is running. */
  check(): void {
    this.follow(response => isRunning(response));
  }

  /** The Sync banner's button finished. Unless the user cancelled the confirm, follow the Sync it started. */
  onSyncCommandFinished(response: PlatformCommandResponse): void {
    if (!response?.fieldValues?.['ShowConfirmationMessage']) {
      const before = this.shown;
      this.follow((answer, ask) => isRunning(answer) || (ask < asksPerFollow && showsSameBar(answer, before)));
    }
  }

  private follow(keepAsking: (response: any, ask: number) => boolean): void {
    this.securityStore.select(getClaims).pipe(take(1)).subscribe(claims => {
      if (claims['WorkspaceId'] && claims['WorkspaceId'].length > 0) {
        this.restart.next();
        let lastAnswered = 0;
        // Ask 0 goes out at once and ask n after n polls, only while the answers say to keep asking.
        interval(WorkspaceSyncPollMs).pipe(
          map(poll => poll + 1),
          startWith(0),
          // Without an answer for a whole follow, stop rather than ask a failing server for ever.
          takeWhile(ask => ask - lastAnswered <= asksPerFollow),
          exhaustMap(ask => this.platformQueryService.executeQuery(workspaceSyncQuery()).pipe(
            take(1),
            tap(() => lastAnswered = ask),
            map(response => ({ ask, response })),
            // A failed ask shows nothing; the next tick asks again.
            catchError(() => EMPTY))),
          takeWhile(({ ask, response }) => keepAsking(response, ask), true),
          takeUntil(merge(this.destroyed, this.restart))
        ).subscribe(({ response }) => {
          this.shown = response;
          this.show(response);
        });
      }
    });
  }
}

function isRunning(response: any): boolean {
  return response?.fieldValues?.['WorkspaceSyncState'] === 'Running';
}

/** Whether two answers show the same Sync bar: the same state and the same message. */
function showsSameBar(response: any, other: any): boolean {
  return response?.fieldValues?.['WorkspaceSyncState'] === other?.fieldValues?.['WorkspaceSyncState']
    && response?.fieldValues?.['WorkspaceSyncMessageBar']?.message === other?.fieldValues?.['WorkspaceSyncMessageBar']?.message;
}

function workspaceSyncQuery(): PlatformQuery {
  const query = new PlatformQuery();
  query.api = Api.AdminPanel;
  query.name = 'WorkspaceSyncQuery';
  return query;
}
